import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

import { catchError, finalize, from, of, take, tap } from 'rxjs';

import { Announcement } from '@shared/announcement/models';
import { iconCodeToString } from '@shared/announcement/constants';
import { DefaultPage } from '@shared/restaurant/enums';
import { AuthStatesEnum } from '../../constants';
import { LoginService } from '../../services';
import { AnnouncementService } from '../../../services';
import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';

@Component({
  selector: 'tablesready-login',
  styleUrls: ['./login.components.scss'],
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit, OnDestroy {
  @Output() toggle = new EventEmitter<AuthStatesEnum>();

  form: UntypedFormGroup;
  icon = AppIcon;
  size = AppIconSize;
  submitting = false;
  authStates = AuthStatesEnum;
  announcement: Announcement | null = null;
  iconCodeToString = iconCodeToString;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly router: Router,
    private readonly loginService: LoginService,
    private readonly announcementsService: AnnouncementService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
  ) {
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      username: ['', [
        Validators.required,
        Validators.maxLength(100),
      ]],
      password: ['', [
        Validators.required,
        Validators.maxLength(100),
      ]],
    });

    this.announcementsService.load()
      .pipe(
        tap(announcement => this.announcement = announcement),
        catchError(() => of(null)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.toggle.complete();
  }

  createAccount(): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { btn: 'login' },
      queryParamsHandling: 'merge',
    }).then(() => this.toggle.emit(this.authStates.signup));
  }

  login() {
    this.loginService.login({
      username: this.form.value.username.trim().toLowerCase(),
      password: this.form.value.password,
    })
      .pipe(
        tap(result => {
          if (result) {
            const { queryParamMap } = this.activatedRoute.snapshot;
            let navigatePath = '/settings/account/profile';
            if (result.location && !queryParamMap.get('navigateToProfile')) {
              if (result.location.publicWaitlist.defaultPage === DefaultPage.reservation) {
                navigatePath = '/reservations';
              } else if (result.location.publicWaitlist.defaultPage === DefaultPage.floorPlan &&
                result.account.features.floorPlan
              ) {
                navigatePath = '/floorplan';
              } else {
                navigatePath = '/waitlist';
              }
            }

            navigatePath += queryParamMap.get('navigateToProfile') ? '?accountsAccessTab=true' : '';

            from(this.router.navigateByUrl(navigatePath))
              .pipe(take(1))
              .subscribe(() => {
              });
          }
        }),
        finalize(() => this.submitting = false),
      )
      .subscribe();
  }

  get usernameControl(): AbstractControl {
    return this.form.get('username');
  }

  get passwordControl(): AbstractControl {
    return this.form.get('password');
  }
}
